import type {
  Card,
  CardPile,
  Color,
  CustomLayout,
  DrawMode,
  GameState,
  Rank,
  Result,
  Suit,
} from "./types.js";
import { err, ok } from "./types.js";

// ============================================================
// Kürzel-Tabellen
// ============================================================

const SUIT_MAP: Record<string, Suit> = {
  H:  "hearts",    // Herz
  K:  "diamonds",  // Karo
  P:  "spades",    // Pik
  Kr: "clubs",     // Kreuz
};

const RANK_MAP: Record<string, Rank> = {
  A:  1,
  "2": 2, "3": 3, "4": 4,  "5": 5,
  "6": 6, "7": 7, "8": 8,  "9": 9, "10": 10,
  B:  11,  // Bube
  D:  12,  // Dame
  Kg: 13,  // König
};

const COLOR_MAP: Record<Suit, Color> = {
  hearts:   "red",
  diamonds: "red",
  spades:   "black",
  clubs:    "black",
};

// ============================================================
// Karten-Code parsen
// ============================================================

/**
 * Parst einen Karten-Code im Format "{farbe}-{wert}".
 *
 * Farb-Kürzel:  H (Herz), K (Karo), P (Pik), Kr (Kreuz)
 * Wert-Kürzel:  A, 2–10, B (Bube), D (Dame), Kg (König)
 *
 * Beispiele: "H-A", "K-3", "P-Kg", "Kr-B", "H-10"
 */
export function parseCardCode(code: string, faceUp = true): Result<Card> {
  const dashIndex = code.indexOf("-");
  if (dashIndex === -1) {
    return err(`Ungültiger Karten-Code "${code}" — kein Bindestrich gefunden`);
  }

  const suitCode = code.slice(0, dashIndex);
  const rankCode = code.slice(dashIndex + 1);

  const suit = SUIT_MAP[suitCode];
  if (suit === undefined) {
    return err(
      `Unbekannte Farbe "${suitCode}" in "${code}". Gültig: H, K, P, Kr`
    );
  }

  const rank = RANK_MAP[rankCode];
  if (rank === undefined) {
    return err(
      `Unbekannter Wert "${rankCode}" in "${code}". Gültig: A, 2-10, B, D, Kg`
    );
  }

  return ok({
    id: `${suit}-${rank}`,
    suit,
    rank,
    color: COLOR_MAP[suit],
    faceUp,
  });
}

// ============================================================
// Layout validieren
// ============================================================

/**
 * Prüft ob das CustomLayout genau alle 52 einzigartigen Karten enthält
 * und jede Tableau-Spalte mindestens eine Karte hat.
 */
export function validateCustomLayout(layout: CustomLayout): Result<void> {
  const allCodes: string[] = [
    ...layout.tableau.flatMap((col) => [...col]),
    ...layout.stock,
  ];

  if (allCodes.length !== 52) {
    return err(
      `Layout hat ${allCodes.length} Karten — erwartet werden genau 52`
    );
  }

  // Alle parsen und auf Duplikate prüfen
  const seen = new Set<string>();
  for (const code of allCodes) {
    const result = parseCardCode(code);
    if (!result.ok) return err(result.error);

    if (seen.has(result.value.id)) {
      return err(`Karte "${result.value.id}" kommt mehrfach vor (Code: "${code}")`);
    }
    seen.add(result.value.id);
  }

  return ok(undefined);
}

// ============================================================
// Custom GameState erstellen
// ============================================================

/**
 * Erstellt einen GameState aus einem CustomLayout.
 *
 * Tableau: Letzte Karte pro Spalte = face-up, alle anderen verdeckt.
 * Stock:   Die erste Karte in `layout.stock` wird als erstes gezogen.
 *
 * @returns Result<GameState> — enthält Fehler wenn das Layout ungültig ist
 */
export function createCustomGameState(
  gameId: string,
  layout: CustomLayout,
  drawMode: DrawMode = 1
): Result<GameState> {
  const validation = validateCustomLayout(layout);
  if (!validation.ok) return err(validation.error);

  // Tableau aufbauen — letzte Karte je Spalte = face-up
  const tableauColumns: CardPile[] = [];
  for (const columnCodes of layout.tableau) {
    const column: Card[] = [];
    for (let i = 0; i < columnCodes.length; i++) {
      const isFaceUp = i === columnCodes.length - 1;
      const result = parseCardCode(columnCodes[i]!, isFaceUp);
      if (!result.ok) return err(result.error);
      column.push(result.value);
    }
    tableauColumns.push(column);
  }

  // Stock aufbauen — User gibt von oben (erster Zug) nach unten an.
  // Intern: letztes Element im Array = oberste Karte (wird zuerst gezogen).
  // Daher: User-Liste rückwärts einfügen.
  const stock: Card[] = [];
  for (let i = layout.stock.length - 1; i >= 0; i--) {
    const result = parseCardCode(layout.stock[i]!, false);
    if (!result.ok) return err(result.error);
    stock.push(result.value);
  }

  return ok({
    gameId,
    seed: 0, // kein Seed bei manuell definiertem Layout
    drawMode,
    stock,
    waste: [],
    foundation: [[], [], [], []],
    tableau: [
      tableauColumns[0]!,
      tableauColumns[1]!,
      tableauColumns[2]!,
      tableauColumns[3]!,
      tableauColumns[4]!,
      tableauColumns[5]!,
      tableauColumns[6]!,
    ],
    moves: 0,
    elapsedSeconds: 0,
    undoCount: 0,
    shakeCount: 0,
    flipCount: 0,
    stockRecycleCount: 0,
    status: "idle",
    history: [],
    layoutSource: layout, // für Undo-Replay gespeichert
  });
}
